import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateChapterMutation } from "../../slices/chapterApiSlice";
import { useGetQuestionBankSubjectsQuery } from "../../slices/questionBankSubjectApiSlice";

const CreateChapterScreen = () => {
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [order, setOrder] = useState(1);

  const { data: subjects = [] } = useGetQuestionBankSubjectsQuery();

  const [createChapter, { isLoading }] = useCreateChapterMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!subject || !name.trim()) {
      toast.error("Subject and chapter name are required");
      return;
    }

    try {
      await createChapter({
        subject,
        name: name.trim(),
        description: description.trim(),
        order: Number(order),
      }).unwrap();

      toast.success("Chapter created successfully");

      navigate("/chapters");
    } catch (err) {
      const message =
        err?.data?.message || err?.error || "Failed to create chapter";

      switch (message) {
        case "Subject is required":
          toast.error("Please select a subject");
          break;

        case "Chapter name is required":
          toast.error("Please enter a chapter name");
          break;

        case "Question bank subject not found":
          toast.error("Selected subject does not exist");
          break;

        case "Chapter already exists in this subject":
          toast.error("A chapter with this name already exists");
          break;

        default:
          toast.error(message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/chapters"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Chapters
        </Link>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg border border-white/30 shadow-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800">Create Chapter</h1>

            <p className="mt-2 text-gray-500">
              Add a new chapter to the question bank
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Creating chapter...
            </div>
          )}

          <form onSubmit={submitHandler} className="grid grid-cols-1 gap-6">
            {/* Subject */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Subject *
              </label>

              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              >
                <option value="">Select Subject</option>

                {subjects.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter Name */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Chapter Name *
              </label>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter chapter name"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Order */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Display Order
              </label>

              <input
                type="number"
                min="1"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Description */}

            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Description
              </label>

              <textarea
                rows="5"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter chapter description (optional)"
                className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Submit */}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-4 text-lg font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60"
              >
                {isLoading ? "Creating..." : "Create Chapter"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateChapterScreen;
